import {
  queryOptions,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import {
  activateTournamentMatch,
  setTournamentMatchAutoStart,
  assignTournamentSlot,
  createTournament,
  deleteTournament,
  finishTournament,
  getTournament,
  getTournamentContext,
  listModeratorOptions,
  listTournaments,
  setTournamentFinalWinner,
  startTournament,
  setTournamentMatchWalkover,
  updateTournament,
} from '@/modules/tournament/tournament.service'
import { TOURNAMENT_QUERY_KEYS } from '@/modules/tournament/tournament.constants'
import type {
  TCreateTournamentRequest,
  TTournamentDetail,
  TUpdateTournamentRequest,
} from '@/modules/tournament/tournament.types'
import type { TAdminListParams } from '@/core/lib/table-query'

type TMutationError = AxiosError<{ code?: string; message?: string }>

export const tournamentListQueryOptions = (params: TAdminListParams) =>
  queryOptions({
    queryKey: TOURNAMENT_QUERY_KEYS.list(params),
    queryFn: () => listTournaments(params),
    staleTime: 1000 * 30,
  })

export const tournamentDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TOURNAMENT_QUERY_KEYS.detail(id),
    queryFn: () => getTournament(id),
    staleTime: 1000 * 15,
    // Match results land async (Go lobby-worker), no live push. Poll while the
    // bracket can still change so status/winners flip without a manual refresh.
    refetchInterval: (query) =>
      query.state.data?.status === 'completed' ? false : 1000 * 10,
  })

export const tournamentModeratorOptionsQueryOptions = () =>
  queryOptions({
    queryKey: TOURNAMENT_QUERY_KEYS.moderatorOptions,
    queryFn: listModeratorOptions,
    staleTime: 1000 * 60 * 10,
  })

// One call backing the create/edit form: teams + moderators + custom game
// modes. Use this instead of fetching those three separately.
export const tournamentContextQueryOptions = () =>
  queryOptions({
    queryKey: TOURNAMENT_QUERY_KEYS.context,
    queryFn: getTournamentContext,
    staleTime: 1000 * 60 * 5,
  })

export const useCreateTournament = (
  options?: UseMutationOptions<
    TTournamentDetail,
    TMutationError,
    TCreateTournamentRequest
  >
) => useMutation({ mutationFn: createTournament, ...(options ?? {}) })

export const useUpdateTournament = (
  options?: UseMutationOptions<
    TTournamentDetail,
    TMutationError,
    { id: string; body: TUpdateTournamentRequest }
  >
) =>
  useMutation({
    mutationFn: ({ id, body }) => updateTournament(id, body),
    ...(options ?? {}),
  })

export const useDeleteTournament = (
  options?: UseMutationOptions<void, TMutationError, string>
) => useMutation({ mutationFn: deleteTournament, ...(options ?? {}) })

export const useStartTournament = (
  options?: UseMutationOptions<TTournamentDetail, TMutationError, string>
) => useMutation({ mutationFn: startTournament, ...(options ?? {}) })

export const useFinishTournament = (
  options?: UseMutationOptions<TTournamentDetail, TMutationError, string>
) => useMutation({ mutationFn: finishTournament, ...(options ?? {}) })

export const useAssignTournamentSlot = (
  options?: UseMutationOptions<
    TTournamentDetail,
    TMutationError,
    { tournamentId: string; matchId: string; slot: 1 | 2; teamId: string }
  >
) =>
  useMutation({
    mutationFn: ({ tournamentId, matchId, slot, teamId }) =>
      assignTournamentSlot(tournamentId, matchId, { slot, team_id: teamId }),
    ...(options ?? {}),
  })

export const useSetTournamentFinalWinner = (
  options?: UseMutationOptions<
    TTournamentDetail,
    TMutationError,
    { tournamentId: string; matchId: string; teamId: string }
  >
) =>
  useMutation({
    mutationFn: ({ tournamentId, matchId, teamId }) =>
      setTournamentFinalWinner(tournamentId, matchId, { team_id: teamId }),
    ...(options ?? {}),
  })

export const useSetTournamentMatchWalkover = (
  options?: UseMutationOptions<
    TTournamentDetail,
    TMutationError,
    { tournamentId: string; matchId: string; winnerTeamId: string }
  >
) =>
  useMutation({
    mutationFn: ({ tournamentId, matchId, winnerTeamId }) =>
      setTournamentMatchWalkover(tournamentId, matchId, winnerTeamId),
    ...(options ?? {}),
  })

export const useActivateTournamentMatch = (
  options?: UseMutationOptions<
    TTournamentDetail,
    TMutationError,
    { tournamentId: string; matchId: string }
  >
) =>
  useMutation({
    mutationFn: ({ tournamentId, matchId }) =>
      activateTournamentMatch(tournamentId, matchId),
    ...(options ?? {}),
  })

export const useSetTournamentMatchAutoStart = (
  options?: UseMutationOptions<
    TTournamentDetail,
    TMutationError,
    { tournamentId: string; matchId: string; enabled: boolean }
  >
) =>
  useMutation({
    mutationFn: ({ tournamentId, matchId, enabled }) =>
      setTournamentMatchAutoStart(tournamentId, matchId, enabled),
    ...(options ?? {}),
  })
